import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { METROS, METRO_NAMES } from "@/lib/metros";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EvidenceBadge, { EVIDENCE_OPTIONS } from "@/components/EvidenceBadge";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const VERTICALS = ["veteran","psh","rapid_rehousing","transitional","recovery","reentry","dv","youth","behavioral","affordable","rural","senior","disability"];
const RENT_BASIS_TABS = [
  { value: "all", label: "All Bases" },
  { value: "per_property", label: "Per Property" },
  { value: "per_unit", label: "Per Unit" },
  { value: "per_room", label: "Per Room" },
  { value: "per_bed", label: "Per Bed" },
];
const COUNTIES = [
  "Bexar","Comal","Guadalupe","Kendall",
  "Travis","Williamson","Hays","Bastrop","Caldwell",
  "Harris","Fort Bend","Montgomery","Brazoria","Galveston",
  "Dallas","Tarrant","Collin","Denton","Rockwall",
];

export default function Programs() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [vertical, setVertical] = useState("all");
  const [metro, setMetro] = useState("All Metros");
  const [rentBasis, setRentBasis] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name:"", vertical:"veteran", agency:"", funder:"", population:"", geography:[],
    funding_source:"", payment_flow:"", payment_amount:"", eligibility:"", referral_process:"",
    property_requirements:"", regulatory:"", status:"Active", evidence_level:"needs_verification", source_url:"",
  });

  const load = async () => {
    const r = await api.get("/programs");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const metroCounties = METROS[metro] || [];
  const filtered = items.filter(p =>
    (vertical === "all" || p.vertical === vertical) &&
    (rentBasis === "all" || p.rent_basis === rentBasis) &&
    (metro === "All Metros" || (p.geography || []).some(g => metroCounties.includes(g))) &&
    (q === "" || `${p.name} ${p.agency} ${p.funder} ${p.population}`.toLowerCase().includes(q.toLowerCase()))
  );

  const toggleCounty = (c) => {
    setForm(f => ({...f, geography: f.geography.includes(c) ? f.geography.filter(x=>x!==c) : [...f.geography, c]}));
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    await api.post("/programs", form);
    toast.success("Program added");
    setOpen(false); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">CRM · Intelligence</div>
          <h1 className="text-3xl font-bold">Housing Programs</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-program-btn"><Plus className="w-4 h-4 mr-1" /> Add Program</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Program</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="prog-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
              <div><Label>Vertical</Label>
                <Select value={form.vertical} onValueChange={v=>setForm({...form, vertical:v})}>
                  <SelectTrigger data-testid="prog-vertical"><SelectValue/></SelectTrigger>
                  <SelectContent>{VERTICALS.map(v=><SelectItem key={v} value={v} className="capitalize">{v.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Evidence Level</Label>
                <Select value={form.evidence_level} onValueChange={v=>setForm({...form, evidence_level:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{EVIDENCE_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Agency</Label><Input value={form.agency} onChange={e=>setForm({...form, agency:e.target.value})}/></div>
              <div><Label>Funder</Label><Input value={form.funder} onChange={e=>setForm({...form, funder:e.target.value})}/></div>
              <div className="col-span-2"><Label>Population Served</Label><Input value={form.population} onChange={e=>setForm({...form, population:e.target.value})}/></div>
              <div className="col-span-2"><Label>Counties</Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {COUNTIES.map(c => (
                    <button key={c} type="button" onClick={()=>toggleCounty(c)}
                      className={`px-3 py-1 rounded-full text-xs border ${form.geography.includes(c) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2"><Label>Payment Flow</Label><Input value={form.payment_flow} onChange={e=>setForm({...form, payment_flow:e.target.value})}/></div>
              <div><Label>Payment Amount</Label><Input value={form.payment_amount} onChange={e=>setForm({...form, payment_amount:e.target.value})}/></div>
              <div><Label>Status</Label><Input value={form.status} onChange={e=>setForm({...form, status:e.target.value})}/></div>
              <div className="col-span-2"><Label>Referral Process</Label><Textarea value={form.referral_process} onChange={e=>setForm({...form, referral_process:e.target.value})}/></div>
              <div className="col-span-2"><Label>Property Requirements</Label><Textarea value={form.property_requirements} onChange={e=>setForm({...form, property_requirements:e.target.value})}/></div>
              <div className="col-span-2"><Label>Source URL</Label><Input value={form.source_url} onChange={e=>setForm({...form, source_url:e.target.value})}/></div>
            </div>
            <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="prog-submit">Save Program</Button>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-slate-200 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search programs..." className="pl-9" value={q} onChange={e=>setQ(e.target.value)} data-testid="prog-search" />
        </div>
        <Select value={metro} onValueChange={setMetro}>
          <SelectTrigger className="w-48" data-testid="prog-metro-filter"><SelectValue placeholder="Metro"/></SelectTrigger>
          <SelectContent>{METRO_NAMES.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vertical} onValueChange={setVertical}>
          <SelectTrigger className="w-56" data-testid="prog-vertical-filter"><SelectValue placeholder="All verticals"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All verticals</SelectItem>
            {VERTICALS.map(v=><SelectItem key={v} value={v} className="capitalize">{v.replace("_"," ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 font-mono">{filtered.length} shown</div>
      </Card>

      <div className="flex gap-2 flex-wrap" data-testid="rent-basis-tabs">
        {RENT_BASIS_TABS.map(t => (
          <button
            key={t.value}
            type="button"
            onClick={()=>setRentBasis(t.value)}
            data-testid={`rent-basis-tab-${t.value}`}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${rentBasis === t.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(p => (
          <Link to={`/programs/${p.id}`} key={p.id} className="block group" data-testid={`program-card-${p.id}`}>
            <Card className="p-5 border-slate-200 hover:border-slate-900 transition-colors h-full">
              <div className="flex justify-between items-start mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{p.vertical?.replace("_"," ")}</div>
                <EvidenceBadge level={p.evidence_level} />
              </div>
              <h3 className="text-base font-semibold text-slate-900 group-hover:text-[#C25E00] mb-2">{p.name}</h3>
              <div className="text-xs text-slate-600 space-y-1">
                <div><span className="text-slate-400">Agency:</span> {p.agency || "—"}</div>
                <div><span className="text-slate-400">Funder:</span> {p.funder || "—"}</div>
                <div><span className="text-slate-400">Counties:</span> {(p.geography||[]).join(", ") || "—"}</div>
                <div><span className="text-slate-400">Pmt:</span> {p.payment_amount || "—"}</div>
              </div>
            </Card>
          </Link>
        ))}
        {filtered.length === 0 && <div className="text-slate-500 text-sm">No programs match.</div>}
      </div>
    </div>
  );
}
